import type { Renderer2, Signal } from '@angular/core';
import Color from 'color';

import {
  contrastColor,
  validateColorContrast,
} from '@plugins/client/colors/color-utils';

import type {
  Accessibility,
  AriaInput,
  ColorAccessibility,
  DisplayConfiguration,
  DisplayVariants,
} from '../../public/shared-button/types';

import {
  ARIA_CAMEL_TO_KEBAB_CASE,
  LIGHTNESS_LIMIT,
  PRIMARY_COLORS,
  SHADOW_OPACITY,
  TEXT_HOVER_OPACITY,
} from './constants';

/** If we pass a boolean value, stringify it, otherwise return the value unchanged. */
const stringifyBool = (val: string | boolean | undefined): string => {
  if (val === undefined) return 'false';
  return typeof val === 'boolean' ? `${val.toString()}` : val;
};

/** For strings, we just check their truthiness For other values (booleans),
 * we check if the value is set as undefined. */
const isAriaPropDefined = (arg: unknown): boolean =>
  typeof arg === 'string' ? !!arg : arg !== undefined;

const buildCssVar = (...parts: string[]) => `--${parts.join('-')}`;

const twoDecimals = (val: number): number => {
  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(val);
  return Number(formatted);
};

export const interpretOpacity = (val: string): number => {
  const initialConversion = twoDecimals(Number(val));
  if (initialConversion) {
    return initialConversion > 1 ? 1 : initialConversion;
  }
  const pctReg = /^\d+(\.\d+)?%$/;
  const trimmed = val.replaceAll(' ', '').trim();
  if (pctReg.test(trimmed)) {
    const unboundConvert = Number(trimmed.replace('%', '')) / 100;
    if (Number.isNaN(unboundConvert)) return TEXT_HOVER_OPACITY;
    const converted = twoDecimals(unboundConvert);
    return converted > 1 ? 1 : converted;
  }
  return TEXT_HOVER_OPACITY;
};

/**  Hover should darken light colors and brighten dark ones
 * if explicit CSS value is not provided. */
const primaryHoverBrightness = (
  lightness: number,
  palette: DisplayConfiguration,
): string => {
  if (palette['primary-hover-brightness'])
    return palette['primary-hover-brightness'];
  return lightness > LIGHTNESS_LIMIT ? '95%' : '110%';
};

export const setCssVariables = (
  palette: DisplayConfiguration,
  currentVariant: DisplayVariants | null,
  colorAccessibility: Signal<ColorAccessibility>,
): Record<string, string> => {
  // Don't let empty strings pass through
  const keys = Object.keys(palette).filter(
    (key) => key in palette,
  ) as (keyof DisplayConfiguration)[];
  if (!keys.length) return {};

  const { text, minimum, ...contrastOptions } = colorAccessibility();

  const style = {} as Record<string, string>;
  keys.forEach((key) => (style[`--${key}`] = palette[key] as string));

  const primaryKey = keys.find((key) =>
    PRIMARY_COLORS.some(
      (primaryKey) => key === primaryKey && key === currentVariant,
    ),
  );
  const outlineHoverOpacity = 'outline-hover-opacity';
  if (palette[outlineHoverOpacity]) {
    style[buildCssVar(outlineHoverOpacity)] = interpretOpacity(
      palette[outlineHoverOpacity],
    ).toString();
  }
  if (!primaryKey) return style;

  const color = palette[primaryKey] as string;
  const cssVar = (val: string) => buildCssVar(primaryKey, val);

  style[cssVar('text')] = contrastColor(color, {
    contrastRatio: text,
    ...contrastOptions,
  });
  style[cssVar('shadow')] = Color(color)
    .hsl()
    .lightness(30)
    .alpha(SHADOW_OPACITY)
    .toString();

  const [h, s, l] = Color(color).hsl().array();

  style[buildCssVar('primary', 'hover', 'brightness')] = primaryHoverBrightness(
    l,
    palette,
  );
  // A maximum of 75% brightness is applied to outline-hover
  style[cssVar('outline-hover')] = `hsla(${h}, ${s.toFixed(2)}%, ${Math.min(
    Number(l.toFixed(2)),
    75,
  )}%, var(--${outlineHoverOpacity}))`;

  style[cssVar('border')] = validateColorContrast(color, {
    contrastRatio: minimum,
  });
  return style;
};

const getVariant = (variant: DisplayVariants) =>
  variant.includes('plugins-') ? variant : variant.split('-')[0];

export const overrideColor = (
  variant: DisplayVariants,
  color: string,
): Record<string, string> => {
  try {
    return { [getVariant(variant)]: Color(color).toString() };
  } catch {
    return {};
  }
};

/** Set CSS variables in response to component input changes, respecting the priority
 * order (color - config - injection token).
 *
 * Fall back to next highest priority when one of the inputs changes to a falsy value. */
export const handleButtonConfigurationChanges = ({
  injectedTheme,
  config,
  color,
  currentVariant,
  colorAccessibility,
}: {
  injectedTheme: DisplayConfiguration;
  config: DisplayConfiguration | undefined;
  currentVariant: DisplayVariants | null;
  color: string | undefined;
  colorAccessibility: Signal<ColorAccessibility>;
}): Record<string, string> => {
  let colorConfig = {} as Record<string, string>;
  const injectedConfig =
    injectedTheme && Object.keys(injectedTheme).length ? injectedTheme : {};
  const isColorValid = color && currentVariant;
  // For truthy color changes we just override and return
  if (isColorValid) {
    colorConfig = overrideColor(currentVariant, color);
  }

  const updatedConfig = {
    ...injectedConfig,
    ...config,
    ...colorConfig,
  };
  return setCssVariables(updatedConfig, currentVariant, colorAccessibility);
};

export const handleAriaChanges = ({
  currentValue,
  previousValue,
  nativeButton,
  renderer,
}: {
  currentValue?: AriaInput;
  previousValue?: AriaInput;
  nativeButton: HTMLElement;
  renderer: Renderer2;
}): AriaInput => {
  const ARIA_LABEL_KEY_PRESERVE = 'ariaLabel';
  const prevKeys = Object.keys(previousValue ?? {}) as (keyof Accessibility)[],
    currentKeys = Object.keys(currentValue ?? {}) as (keyof Accessibility)[];
  const filteredCurrent = currentKeys.filter((key) =>
    isAriaPropDefined(currentValue?.[key]),
  );
  const missing = prevKeys.filter((key) => !filteredCurrent.includes(key));
  if (missing.length) {
    missing.forEach((att) => {
      if (att === ARIA_LABEL_KEY_PRESERVE) return;
      renderer.removeAttribute(nativeButton, ARIA_CAMEL_TO_KEBAB_CASE[att]);
    });
  }
  filteredCurrent.forEach((att) =>
    renderer.setAttribute(
      nativeButton,
      ARIA_CAMEL_TO_KEBAB_CASE[att],
      stringifyBool(currentValue?.[att]),
    ),
  );
  return currentValue;
};
